import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import api from 'api';
import { DealRoom } from 'models/DealRoom';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import useEffectSkipFirst from 'hooks/useEffectSkipFirst';
import { getMixpanelErrorProps } from 'axiosUtils';
import { ErrorViewedProps } from 'models/MixpanelEvents';
import { SEARCH_ROUTE } from 'data/global';
import AuthContext from 'providers/Auth/Auth.context';
import SearchContext from './Search.context';

const MAX_SAVED_SEARCHES = 10;

const SearchProvider: React.FC = (props) => {
  const { children } = props;

  const { pathname } = useLocation();

  const isSearchPage = useMemo(() => pathname === SEARCH_ROUTE, [pathname]);

  const [isLoading, setIsLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(isSearchPage);

  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [recentlyViewedDeals, setRecentlyViewedDeals] = useState<DealRoom[]>(
    [],
  );

  const { isLoggedIn } = useContext(AuthContext);

  const getMixpanelProps = useGetMixpanelProps();

  const openSearch = useCallback(() => setIsSearchOpen(true), []);

  const toggleSearch = useCallback(() => setIsSearchOpen((prev) => !prev), []);

  const addRecentSearch = useCallback(
    (term: string) => {
      if (!term || !isLoggedIn) return;

      setRecentSearches((prev) =>
        [term, ...prev.filter((search) => search !== term)].slice(
          0,
          MAX_SAVED_SEARCHES,
        ),
      );
    },
    [isLoggedIn],
  );

  const removeRecentSearch = useCallback(
    async (term: string) => {
      setRecentSearches((prev) => prev.filter((search) => search !== term));

      try {
        await api.general.deleteSearchHistoryItem(term);
      } catch (e) {
        mixpanelService.errorViewed(
          getMixpanelProps(getMixpanelErrorProps(e)) as ErrorViewedProps,
        );
      }
    },
    [getMixpanelProps],
  );

  const clearRecentSearches = useCallback(async () => {
    setRecentSearches([]);

    try {
      await api.general.clearSearchHistory();
    } catch (e) {
      mixpanelService.errorViewed(
        getMixpanelProps(getMixpanelErrorProps(e)) as ErrorViewedProps,
      );
    }
  }, [getMixpanelProps]);

  const fetchRecentlyViewedDeals = useCallback(async () => {
    try {
      const { data } = await api.general.getRecentlyViewedDeals();
      setRecentlyViewedDeals(data);
    } catch (e) {
      setRecentlyViewedDeals([]);
    }
  }, []);

  const fetchSearchHistory = useCallback(async () => {
    try {
      const { data } = await api.general.getSearchHistory();
      setRecentSearches(data);
    } catch (e) {
      setRecentSearches([]);
    }
  }, []);

  useEffect(() => {
    if (!isLoggedIn) {
      setRecentSearches([]);
      setRecentlyViewedDeals([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    Promise.all([fetchRecentlyViewedDeals(), fetchSearchHistory()]).finally(
      () => setIsLoading(false),
    );
  }, [isLoggedIn, fetchRecentlyViewedDeals, fetchSearchHistory]);

  useEffectSkipFirst(() => {
    if (isLoggedIn && isSearchPage) fetchRecentlyViewedDeals();
  }, [isLoggedIn, pathname, isSearchPage, fetchRecentlyViewedDeals]);

  useEffectSkipFirst(() => {
    if (!isSearchPage) setIsSearchOpen(false);
  }, [isSearchPage, pathname]);

  return (
    <SearchContext.Provider
      value={{
        isLoading,
        isSearching,
        isSearchOpen,
        isSearchPage,
        recentSearches,
        recentlyViewedDeals,
        addRecentSearch,
        removeRecentSearch,
        clearRecentSearches,
        setIsSearching,
        openSearch,
        toggleSearch,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};

export default SearchProvider;
